import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Hourglass, Search, Timer, Calculation, Delivery, ToolKit, Information } from '@carbon/icons-react';
import {
    Stack,
    DatePicker,
    DatePickerInput,
    Button,
    Grid,
    Column,
    ComboBox,
} from '@carbon/react';
import Card from '../../components/Layouts/Card';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '@carbon/react';
import Typography from '../../components/Basics/Typography';
import InformationContainer from '../../components/Basics/Information.container';
import { CardComponent } from '../../components/Layouts/Card.styles';
import TimeBarChart from '../../components/Graphs/TimeBarChart.container';
import TimeExpansionTable from '../../components/Basics/TimeExpandableTable.container';
import HorizontalBoxPLot from '../../components/Graphs/HorizontalBoxPlot.container';
import { onGetTimesThunk, onGetTimesGroupThunk } from './Times.actions';
import LoadingContent from '../../components/Animations/LoadingContent';
import StackedBar from '../../components/Graphs/StackedBarChart';
import ProductiveTimeLineChart from '../../components/Basics/ProductiveTimeLineChart';
import TurnoStackedBarChart from '../../components/Basics/TurnoStackedBarChart';
import TurnoComparisonBarChart from '../../components/Basics/TurnoComparisonBarChart';
import DesviacionesLineChart from '../../components/Basics/DesviacionesLineChart';
import ColacionBoxplotChart from '../../components/Basics/ColacionBoxplotChart';
import ColacionHeatmapChart from '../../components/Basics/ColacionHeatmapChart';

const TimesContainer = () => {
    const dispatch = useDispatch();

    // Estado global desde Redux
    const {
        data: timesData,
        loadingTimes,
        data_group,
        loadingTimesGroup,
        errorTimesGroup,
    } = useSelector((state) => state.times);


    // Filtros
    const [turnos] = useState([
        { id: 'dia', label: 'Turno Día' },
        { id: 'noche', label: 'Turno Noche' },
    ]);
    const [fecha, setFecha] = useState(null);
    const [turno, setTurno] = useState(null);
    const [selectedTab, setSelectedTab] = useState(0);


    // Rango de fechas para el tab "Tiempos Históricos Agrupados"
    const [rangoFechasAgrupado, setRangoFechasAgrupado] = useState({
        start: null,
        end: null,
    });
    const [errorRangoAgrupado, setErrorRangoAgrupado] = useState('');

    // Handler para ejecutar búsqueda manual
    const handleBuscarHistorico = () => {
        if (!fecha || !turno) return;

        const fechaObj = new Date(fecha);
        if (isNaN(fechaObj.getTime())) return;

        dispatch(onGetTimesThunk(fechaObj, turno));
    };

    // Handler para ejecutar búsqueda agrupada (tab 2)
    const handleBuscarAgrupados = () => {
        const { start, end } = rangoFechasAgrupado;

        // Validación básica
        if (!start || !end) return;
        if (errorRangoAgrupado) return; // si hay error de rango, no llamamos

        const startISO = formatDateToISO(start);
        const endISO = formatDateToISO(end);

        if (!startISO || !endISO) return;

        dispatch(onGetTimesGroupThunk(startISO, endISO));
    };

    // ======================
    // DATOS DINÁMICOS (REDUX)
    // ======================

    const TIEMPO_NOMINAL = timesData?.tiempo_nominal || {
        totalHoras: 0,
        segmentos: [],
    };

    const DETALLES_TIEMPOS = timesData?.detalles_tiempos || [];
    console.log(DETALLES_TIEMPOS)

    const HISTOGRAMAS_TIEMPOS = timesData?.histogramas_tiempos || [];

    const rows = (timesData?.rows || []).map((r) => {
        // Función para formatear porcentajes (si existe valor)
        const fmtPct = (v) =>
            typeof v === 'number' ? `${v.toFixed(1)}%` : v ?? '-';

        // Función para formatear fechas legibles
        const fmtDate = (iso) => {
            if (!iso || iso === 'NaT') return '-';
            const d = new Date(iso);
            return `${String(d.getDate()).padStart(2, '0')}-${String(
                d.getMonth() + 1
            ).padStart(2, '0')}-${d.getFullYear()} ${d.getHours()}:${String(
                d.getMinutes()
            ).padStart(2, '0')}`;
        };

        return {
            id: r.id,
            patente: r.patente,
            ciclos: r.ciclos ?? 0,
            inicio: fmtDate(r.inicio),
            termino: fmtDate(r.termino),
            primera: fmtDate(r.primera),
            colacion: fmtPct(r.colacion),
            traslado: fmtPct(r.traslado),
            detenido: fmtPct(r.detenido),
            carga: fmtPct(r.carga),
            descarga: fmtPct(r.descarga),
            detalles: r.detalles || {},
        };
    });

    // =========================================================
    // INSUMOS PARA LOS GRAFICOS AGRUPADOS (TAB 2)
    // =========================================================

    const DATA_STACK_TURNOS = data_group?.stack_turnos ?? [];
    const DATA_TIEMPO_PRODUCTIVO = data_group?.series_tiempo_productivo ?? [];
    const DATA_COMPARACION_TURNOS = data_group?.comparacion_turnos ?? [];
    const DATA_SERIES_DESVIACIONES = data_group?.series_desviaciones ?? [];
    const DATA_COLACION_BOXPLOT = data_group?.colacion_boxplot ?? [];
    const DATA_COLACION_HEATMAP = data_group?.colacion_heatmap ?? [];

    // Helper: fecha/hora actual en zona horaria de Chile
    const getChileNow = () => {
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: 'America/Santiago',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        });

        const parts = formatter.formatToParts(new Date());
        const lookup = Object.fromEntries(parts.map(p => [p.type, p.value]));

        const year = Number(lookup.year);
        const month = Number(lookup.month) - 1; // 0-index
        const day = Number(lookup.day);
        const hour = Number(lookup.hour);
        const minute = Number(lookup.minute);
        const second = Number(lookup.second);

        // Este Date representa la fecha/hora de Chile, pero como "local" del navegador.
        // Para nosotros importa el "día" y la "hora", no el offset real del navegador.
        return new Date(year, month, day, hour, minute, second);
    };

    const initGroupedRangeAndFetch = () => {
        // Usamos la hora de Chile para definir "ayer"
        const chileNow = getChileNow();

        const year = chileNow.getFullYear();
        const month = chileNow.getMonth();
        const day = chileNow.getDate();

        // Ayer
        const end = new Date(year, month, day - 1);
        // 4 días antes de ayer => 5 días en total: [ayer-4, ..., ayer]
        const start = new Date(end);
        start.setDate(end.getDate() - 4);

        // Guardar en estado (para que el DatePicker los muestre)
        setRangoFechasAgrupado({
            start,
            end,
        });
        setErrorRangoAgrupado('');

        // Formato que la Lambda espera
        const startISO = formatDateToISO(start);
        const endISO = formatDateToISO(end);

        if (!startISO || !endISO) return;

        // Llamar al thunk agrupado
        dispatch(onGetTimesGroupThunk(startISO, endISO));
    };

    const handleTabsChange = ({ selectedIndex }) => {
        setSelectedTab(selectedIndex);

        // Si el usuario entra al segundo tab (índice 1)
        if (selectedIndex === 1) {
            // Sólo inicializamos si aún no hay rango seleccionado
            if (!rangoFechasAgrupado.start || !rangoFechasAgrupado.end) {
                initGroupedRangeAndFetch();
            }
        }
    };


    // Helper: formatea Date -> 'YYYY-MM-DD'
    const formatDateToISO = (date) => {
        if (!(date instanceof Date) || isNaN(date.getTime())) return null;

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // 1-12
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    };


    useEffect(() => {
        // 1) Obtener hora actual de Chile
        const chileNow = getChileNow();

        const year = chileNow.getFullYear();
        const month = chileNow.getMonth();
        const day = chileNow.getDate();
        const hour = chileNow.getHours();

        let fechaTurno;
        let turnoId;

        /*
            Turnos:
            - Turno Día:    07:00 a 19:00
            - Turno Noche:  19:00 a 07:00
    
            Queremos el ÚLTIMO TURNO QUE YA TERMINÓ:
    
            - Si hora ∈ [07, 19): está corriendo el turno día de hoy
                -> el último que terminó fue la NOCHE de AYER
            - Si hora ≥ 19: está corriendo la noche de hoy
                -> el último que terminó fue el DÍA de HOY
            - Si hora ∈ [00, 07): está corriendo la noche de AYER
                -> el último que terminó fue el DÍA de AYER
        */

        if (hour >= 7 && hour < 19) {
            // Día en curso -> último completado: noche de ayer
            fechaTurno = new Date(year, month, day - 1);
            turnoId = 'noche';
        } else if (hour >= 19) {
            // Noche en curso -> último completado: día de hoy
            fechaTurno = new Date(year, month, day);
            turnoId = 'dia';
        } else {
            // 00:00 - 06:59 -> Noche de ayer en curso -> último completado: día de ayer
            fechaTurno = new Date(year, month, day - 1);
            turnoId = 'dia';
        }

        // 2) Actualizar los estados locales (para DatePicker y ComboBox)
        setFecha(fechaTurno);
        setTurno(turnoId);

        // 3) Disparar la búsqueda inicial con ese turno
        dispatch(onGetTimesThunk(fechaTurno, turnoId));
    }, [dispatch]);

    // ======================
    // RENDER
    // ======================
    return (
        <Tabs
            activation="manual"
            selectedIndex={selectedTab}
            onChange={handleTabsChange}
        >
            <TabList activation="manual" aria-label="List of tabs">
                <Tab renderIcon={Hourglass}>Tiempos Históricos</Tab>
                <Tab renderIcon={Hourglass}>Tiempos Históricos Agrupados</Tab>
            </TabList>

            <TabPanels>
                <TabPanel style={{ width: '100%' }}>
                    <Stack gap={3}>
                        {/* FILTROS */}
                        <Card background="transparent" flexDirection="column" alignItems="stretch">
                            <Grid condensed fullWidth style={{ width: '100%', alignItems: 'center' }}>
                                {/* Fecha */}
                                <Column lg={3} md={4} sm={12} style={{ marginTop: '-0.5rem' }}>
                                    <DatePicker
                                        datePickerType="single"
                                        dateFormat="d-m-Y"
                                        maxDate={(() => {
                                            const ayer = new Date();
                                            ayer.setDate(ayer.getDate() - 1);
                                            return ayer;
                                        })()}
                                        value={fecha ? [fecha] : []}
                                        onChange={(dates) => setFecha(dates?.[0] ?? null)}
                                    >
                                        <DatePickerInput
                                            id="fecha-ciclos-hoy"
                                            size="md"
                                            labelText="Fecha de los ciclos"
                                            placeholder="dd-mm-yyyy"
                                        />
                                    </DatePicker>
                                </Column>

                                {/* Turno */}
                                <Column lg={3} md={4} sm={12} style={{ marginLeft: '1rem' }}>
                                    <ComboBox
                                        id="turnos-hoy"
                                        size="md"
                                        items={turnos}
                                        itemToString={(item) => (item ? item.label : '')}
                                        titleText="Turno de los Ciclos"
                                        placeholder="Seleccione un turno"
                                        onChange={({ selectedItem }) => setTurno(selectedItem?.id ?? null)}
                                        selectedItem={turnos.find((t) => t.id === turno) || null}
                                    />
                                </Column>

                                {/* Botón */}
                                <Column
                                    lg={2}
                                    md={4}
                                    sm={12}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    <Button
                                        size="md"
                                        kind="primary"
                                        renderIcon={Search}
                                        onClick={handleBuscarHistorico}
                                        disabled={!fecha || !turno || loadingTimes}
                                    >
                                        {loadingTimes ? 'Cargando...' : 'Buscar Ciclos'}
                                    </Button>
                                </Column>
                            </Grid>
                        </Card>

                        {/* LOADER DEBAJO DE LOS FILTROS */}
                        {loadingTimes && (
                            <div
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    minHeight: '50vh',
                                }}
                            >
                                <LoadingContent />
                            </div>
                        )}

                        {/* CONTENIDO PRINCIPAL (solo se muestra si ya hay datos) */}
                        {!loadingTimes && (
                            <>

                                {/* ---------------- TÍTULO PRINCIPAL ---------------- */}
                                <Card $justifyContent="flex-start">
                                    <Timer size={18} />
                                    <Typography className="cds--type-heading-compact-01">
                                        ÁRBOL DE DISPONIBILIDAD
                                    </Typography>
                                </Card>

                                {/* ---------------- BLOQUE PRINCIPAL (barra de tiempo nominal) ---------------- */}
                                <Card
                                    $justifyContent="flex-start"
                                    flexDirection="column"
                                    style={{
                                        padding: '1rem',
                                        gap: '0.75rem',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'flex-start',
                                        textAlign: 'left',
                                        width: '100%',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '100%',
                                            textAlign: 'left',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                        }}
                                    >
                                        <Calculation size={24} />
                                        <Typography
                                            className="cds--type-heading-compact-01"
                                            style={{ fontWeight: 'bold' }}
                                        >
                                            TIEMPO CALENDARIO (USO DEL TIEMPO)
                                        </Typography>
                                    </div>

                                    {/* Total de horas dinámico */}
                                    <div style={{ width: '100%', textAlign: 'left' }}>
                                        <span style={{ color: '#C6C6C6', fontSize: '0.8rem' }}>
                                            {TIEMPO_NOMINAL?.totalHoras ?? 0} HRS (12h × cantidad de equipos)
                                        </span>
                                    </div>

                                    {/* Barra de colores dinámica */}
                                    <div
                                        style={{
                                            display: 'flex',
                                            width: '100%',
                                            height: '1rem',
                                            borderRadius: '2px',
                                            overflow: 'hidden',
                                            marginTop: '0.25rem',
                                        }}
                                    >
                                        {(TIEMPO_NOMINAL?.segmentos || []).map((segmento, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    width: `${segmento.porcentaje}%`,
                                                    backgroundColor: segmento.color,
                                                }}
                                            ></div>
                                        ))}
                                    </div>

                                    {/* Leyenda dinámica */}
                                    <div
                                        style={{
                                            display: 'flex',
                                            gap: '2rem',
                                            marginTop: '0.5rem',
                                            alignSelf: 'flex-start',
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        {(TIEMPO_NOMINAL?.segmentos || []).map((segmento, index) => (
                                            <div
                                                key={index}
                                                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                                            >
                                                <div
                                                    style={{
                                                        width: '12px',
                                                        height: '12px',
                                                        backgroundColor: segmento.color,
                                                    }}
                                                ></div>
                                                <span style={{ color: '#C6C6C6', fontSize: '0.8rem' }}>
                                                    {segmento.label}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </Card>


                                {/* ---------------- TARJETAS DE DETALLE ---------------- */}
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'stretch',
                                        width: '100%',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    {DETALLES_TIEMPOS.map((card, index) => (
                                        <div
                                            key={card.id || index}
                                            style={{
                                                flex: '0 0 20.0%',
                                                maxWidth: '20.0%',
                                                minWidth: '200px',
                                                display: 'flex',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Card
                                                flexDirection="column"
                                                style={{
                                                    width: '100%',
                                                    padding: '1rem',
                                                    gap: '0.5rem',
                                                    display: 'flex',
                                                    justifyContent: 'flex-start',
                                                    alignItems: 'flex-start',
                                                    height: '100%',
                                                    minHeight: '220px',
                                                    color: card.textColor,
                                                    border: `1px solid ${card.borderColor}`,
                                                }}
                                            >
                                                {/* ENCABEZADO */}
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-start',
                                                        gap: '0.5rem',
                                                        width: '100%',
                                                    }}
                                                >
                                                    {/* Icono dinámico según tipo */}
                                                    {card.icon === 'Delivery' ? <Delivery size={20} /> : <ToolKit size={20} />}
                                                    <Typography
                                                        className="cds--type-heading-compact-01"
                                                        style={{
                                                            fontWeight: 'bold',
                                                            color: card.textColor,
                                                            textTransform: 'uppercase',
                                                        }}
                                                    >
                                                        {card.id === 'efectivo'
                                                            ? card.title
                                                            : `${card.title} ${card.porcentaje}%`}

                                                    </Typography>
                                                </div>

                                                {/* OBJETIVO */}
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        display: 'flex',
                                                        justifyContent: 'flex-start',
                                                        marginTop: '0.25rem',
                                                    }}
                                                >
                                                    <Typography
                                                        style={{
                                                            fontSize: '0.8rem',
                                                            color: card.textColor,
                                                            textAlign: 'left',
                                                        }}
                                                    >
                                                        Objetivo: {card.id === 'efectivo'
                                                            ? ''
                                                            : card.objetivo}
                                                    </Typography>
                                                </div>

                                                {/* TABLA DE DETALLES */}
                                                <table
                                                    style={{
                                                        width: '100%',
                                                        color: card.textColor,
                                                        fontSize: '0.8rem',
                                                        wordWrap: 'break-word',
                                                        tableLayout: 'fixed',
                                                        borderCollapse: 'collapse',
                                                        marginTop: '0.25rem',
                                                    }}
                                                >
                                                    <tbody>
                                                        {card.rows.map((row, i) => (
                                                            <tr
                                                                key={i}
                                                                style={{
                                                                    borderTop: i === 0 ? 'none' : `1px solid ${card.borderColor}`,
                                                                    borderBottom: `1px solid ${card.borderColor}`,
                                                                }}
                                                            >
                                                                <td
                                                                    style={{
                                                                        width: '60%',
                                                                        padding: '6px 0',
                                                                        // agrega sangría solo desde la segunda fila
                                                                        paddingLeft: i > 0 ? '1.5rem' : '0',
                                                                    }}
                                                                >
                                                                    {row.label}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        width: '20%',
                                                                        textAlign: 'right',
                                                                        padding: '6px 0',
                                                                    }}
                                                                >
                                                                    {row.porcentaje}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        width: '20%',
                                                                        textAlign: 'right',
                                                                        padding: '6px 0',
                                                                    }}
                                                                >
                                                                    {row.horas}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </Card>
                                        </div>
                                    ))}
                                </div>


                                {/* ==================== HISTOGRAMAS ==================== */}
                                <Card $justifyContent="flex-start">
                                    <Timer size={18} />
                                    <Typography className="cds--type-heading-compact-01">
                                        TIEMPOS DE JORNADA - HISTOGRAMA
                                    </Typography>
                                </Card>

                                <InformationContainer description="Cada barra representa la cantidad de conductores en un intervalo horario." />

                                <CardComponent width="100%">
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(2, 1fr)',
                                        }}
                                    >
                                        {HISTOGRAMAS_TIEMPOS.map((hist) => (
                                            <Card
                                                key={hist.id}
                                                flexDirection="column"
                                                style={{
                                                    backgroundColor: '#161616',
                                                    border: '1px solid #393939',
                                                    minHeight: '360px',
                                                    paddingBottom: '1rem',
                                                }}
                                            >
                                                {hist.type === 'boxplot' ? (
                                                    <HorizontalBoxPLot data={hist.data} title={hist.title} />
                                                ) : (
                                                    <StackedBar data={hist.data} title={hist.title} />
                                                )}
                                            </Card>
                                        ))}
                                    </div>
                                </CardComponent>

                                {/* ==================== TABLA FINAL ==================== */}
                                <Card $justifyContent="flex-start">
                                    <Timer size={18} />
                                    <Typography className="cds--type-heading-compact-01">
                                        TIEMPOS POR EQUIPO
                                    </Typography>
                                </Card>

                                <InformationContainer description="Los porcentajes representan el tiempo individual de cada equipo." />
                                <TimeExpansionTable headers={[
                                    { key: 'patente', header: 'Patente' },
                                    { key: 'ciclos', header: 'Ciclos' },
                                    { key: 'inicio', header: 'Inicio efectivo' },
                                    { key: 'termino', header: 'Término efectivo' },
                                    { key: 'primera', header: 'Primera carga' },
                                    { key: 'colacion', header: 'Colación' },
                                    { key: 'traslado', header: 'Traslado' },
                                    { key: 'detenido', header: 'Detenido' },
                                    { key: 'carga', header: 'Carga' },
                                    { key: 'descarga', header: 'Descarga' },
                                ]} rows={rows} />

                            </>
                        )}
                    </Stack>
                </TabPanel>
                <TabPanel style={{ width: '100%' }}>
                    <Stack gap={3}>
                        {/* =============== FILTROS =============== */}
                        <Card background="transparent" flexDirection="column" alignItems="stretch">
                            <Grid condensed fullWidth style={{ width: '100%', alignItems: 'center' }}>
                                {/* Rango de fechas */}
                                <Column lg={3} md={4} sm={12} style={{ marginTop: '-0.5rem' }}>
                                    <DatePicker
                                        datePickerType="range"
                                        dateFormat="d-m-Y"
                                        maxDate={(() => {
                                            const ayer = new Date();
                                            ayer.setDate(ayer.getDate() - 1);
                                            return ayer;
                                        })()}
                                        value={
                                            rangoFechasAgrupado.start && rangoFechasAgrupado.end
                                                ? [rangoFechasAgrupado.start, rangoFechasAgrupado.end]
                                                : []
                                        }
                                        onChange={(dates) => {
                                            if (!Array.isArray(dates) || dates.length === 0) {
                                                setRangoFechasAgrupado({ start: null, end: null });
                                                setErrorRangoAgrupado('');
                                                return;
                                            }

                                            const [start, end] = dates;

                                            setRangoFechasAgrupado({
                                                start: start instanceof Date ? start : null,
                                                end: end instanceof Date ? end : null,
                                            });
                                            setErrorRangoAgrupado('');

                                            if (
                                                start instanceof Date &&
                                                !isNaN(start.getTime()) &&
                                                end instanceof Date &&
                                                !isNaN(end.getTime())
                                            ) {
                                                const MS_PER_DAY = 1000 * 60 * 60 * 24;
                                                const diffDays =
                                                    Math.floor((end.getTime() - start.getTime()) / MS_PER_DAY) + 1;

                                                if (diffDays > 10) {
                                                    setErrorRangoAgrupado('El rango máximo permitido es de 10 días.');
                                                }
                                            }
                                        }}
                                    >
                                        <DatePickerInput
                                            id="fecha-ciclos-agrupados-start"
                                            size="md"
                                            labelText="Fecha inicio"
                                            placeholder="dd-mm-yyyy"
                                        />
                                        <DatePickerInput
                                            id="fecha-ciclos-agrupados-end"
                                            size="md"
                                            labelText="Fecha término"
                                            placeholder="dd-mm-yyyy"
                                        />
                                    </DatePicker>

                                    {errorRangoAgrupado && (
                                        <Typography
                                            style={{
                                                color: '#fa4d56',
                                                fontSize: '0.75rem',
                                                marginTop: '0.25rem',
                                            }}
                                        >
                                            {errorRangoAgrupado}
                                        </Typography>
                                    )}
                                </Column>

                                {/* Botón */}
                                <Column
                                    lg={2}
                                    md={4}
                                    sm={12}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    <Button
                                        size="md"
                                        kind="primary"
                                        renderIcon={Search}
                                        onClick={handleBuscarAgrupados}
                                        disabled={
                                            !rangoFechasAgrupado.start ||
                                            !rangoFechasAgrupado.end ||
                                            !!errorRangoAgrupado
                                        }
                                    >
                                        Buscar Turnos
                                    </Button>
                                </Column>
                            </Grid>
                        </Card>
                        {/* =============== CONTENIDO PRINCIPAL TAB 2 =============== */}
                        {loadingTimesGroup ? (
                            // Mientras carga: spinner centrado, igual que en el tab 1
                            <div
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    minHeight: '50vh',
                                }}
                            >
                                <LoadingContent />
                            </div>
                        ) : (
                            <>
                                {/* =============== BLOQUE PRINCIPAL GRAFICOS DE TIEMPOS =============== */}
                                <Card $justifyContent="flex-start">
                                    <Timer size={18} />
                                    <Typography className="cds--type-heading-compact-01">
                                        TIEMPOS DE JORNADA AGRUPADOS
                                    </Typography>
                                </Card>

                                <InformationContainer description="Evolución del tiempo productivo y del uso de la jornada por fecha y turno en el rango seleccionado." />

                                <CardComponent width="100%">
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '1rem',
                                        }}
                                    >
                                        {/* Stacked: uso del tiempo por día/turno */}
                                        <Card
                                            flexDirection="column"
                                            style={{
                                                backgroundColor: '#161616',
                                                border: '1px solid #393939',
                                                minHeight: '360px',
                                                paddingBottom: '1rem',
                                            }}
                                        >
                                            <TurnoStackedBarChart
                                                data={DATA_STACK_TURNOS}
                                                title="Desglose de jornada (horas) por día y turno"
                                            />
                                        </Card>

                                        {/* Línea: tiempo productivo por turno */}
                                        <Card
                                            flexDirection="column"
                                            style={{
                                                backgroundColor: '#161616',
                                                border: '1px solid #393939',
                                                minHeight: '360px',
                                                paddingBottom: '1rem',
                                            }}
                                        >
                                            <ProductiveTimeLineChart
                                                data={DATA_TIEMPO_PRODUCTIVO}
                                                title="Evolución de tiempo de trabajo (horas) por turno"
                                            />
                                        </Card>

                                        {/* Comparación día vs noche */}
                                        <Card
                                            flexDirection="column"
                                            style={{
                                                backgroundColor: '#161616',
                                                border: '1px solid #393939',
                                                minHeight: '360px',
                                                paddingBottom: '1rem',
                                            }}
                                        >
                                            <TurnoComparisonBarChart
                                                data={DATA_COMPARACION_TURNOS}
                                                title="Comparación Día vs Noche (tiempo de trabajo en horas)"
                                            />
                                        </Card>

                                        {/* Desviaciones inicio / término */}
                                        <Card
                                            flexDirection="column"
                                            style={{
                                                backgroundColor: '#161616',
                                                border: '1px solid #393939',
                                                minHeight: '360px',
                                                paddingBottom: '1rem',
                                            }}
                                        >
                                            <DesviacionesLineChart
                                                data={DATA_SERIES_DESVIACIONES}
                                                title="Desviaciones de inicio y término efectivo en horas por turno"
                                            />
                                        </Card>
                                    </div>
                                </CardComponent>

                                {/* =============== BLOQUE COLACIONES =============== */}
                                <Card $justifyContent="flex-start">
                                    <Timer size={18} />
                                    <Typography className="cds--type-heading-compact-01">
                                        COLACIONES AGRUPADAS
                                    </Typography>
                                </Card>

                                <InformationContainer description="Distribución y promedio de las colaciones por día y turno en el rango seleccionado." />

                                <CardComponent width="100%">
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '1rem',
                                        }}
                                    >
                                        {/* Boxplot colación */}
                                        <Card
                                            flexDirection="column"
                                            style={{
                                                backgroundColor: '#161616',
                                                border: '1px solid #393939',
                                                minHeight: '360px',
                                                paddingBottom: '1rem',
                                            }}
                                        >
                                            <ColacionBoxplotChart
                                                data={DATA_COLACION_BOXPLOT}
                                                title="Distribución de duración de colación (horas)"
                                            />
                                        </Card>

                                        {/* Heatmap colación */}
                                        <Card
                                            flexDirection="column"
                                            style={{
                                                backgroundColor: '#161616',
                                                border: '1px solid #393939',
                                                minHeight: '360px',
                                                paddingBottom: '1rem',
                                            }}
                                        >
                                            <ColacionHeatmapChart
                                                data={DATA_COLACION_HEATMAP}
                                                title="Colación promedio (horas) por día y turno"
                                            />
                                        </Card>
                                    </div>
                                </CardComponent>
                            </>
                        )}
                    </Stack>
                </TabPanel>
            </TabPanels>
        </Tabs >
    );
};

export default TimesContainer;
